import { makeAutoObservable, runInAction } from "mobx";

import {
  getAiCreditsBalance,
  getApiErrorMessage,
  getCurrentSubscription,
  openTeamBillingPortal,
} from "@/api";
import { getBillingReturnPath } from "@/utils/getBillingReturnPath";
import { redirectTo } from "@/utils/redirectTo";

import type { GetAiCreditsBalance200Pools, Subscription } from "@/api";
import type { TeamModel } from "@/models/TeamModel";

export class TeamBillingStore {
  team: TeamModel;

  status: "loading" | "ready" | "error" = "loading";

  subscription: Subscription | null = null;

  /** The user's AI credits on this team for the current period, by pool. */
  creditPools: GetAiCreditsBalance200Pools = {};

  error: string | null = null;

  isOpeningBillingPortal = false;

  private readonly abortController = new AbortController();

  constructor(team: TeamModel) {
    this.team = team;
    makeAutoObservable(this);
  }

  dispose() {
    this.abortController.abort();
  }

  async load() {
    const { signal } = this.abortController;
    const teamUid = this.team.uid;
    this.status = "loading";
    this.error = null;

    try {
      const [{ subscription }, { pools }] = await Promise.all([
        getCurrentSubscription({ teamUid }, { signal }),
        getAiCreditsBalance({ teamUid }, { signal }),
      ]);
      if (signal.aborted) return;

      runInAction(() => {
        this.subscription = subscription ?? null;
        this.creditPools = pools;
        this.status = "ready";
      });
    } catch (err) {
      if (signal.aborted) return;

      runInAction(() => {
        this.error = getApiErrorMessage(err);
        this.status = "error";
      });
    }
  }

  async openBillingPortal() {
    const { signal } = this.abortController;
    this.isOpeningBillingPortal = true;
    this.error = null;

    const result = await redirectTo(
      () =>
        openTeamBillingPortal(
          this.team.permalink,
          { returnPath: getBillingReturnPath(this.team) },
          { signal },
        ),
      signal,
    );
    if (result.isRedirecting) return;

    runInAction(() => {
      this.error = result.error;
      this.isOpeningBillingPortal = false;
    });
  }
}
